import { useCallback, useState } from 'react';

import type { Dispatch, SetStateAction } from 'react';

export interface CopyToClipboardProps {
  /**
   * 클립보드로 복사할 텍스트 값을 전달합니다.
   */
  value: string;
}

export type CopyToClipboardReturn = {
  copyState: CopyState;
  copyToClipboard: () => void;
  setCopyState: Dispatch<SetStateAction<CopyState>>;
};

export type CopyState = {
  error: unknown | null;
  value: string | null;
};

/**
 * @description
 * 클립보드 텍스트 복사 기능을 제공하기 위한 커스텀 훅입니다.
 * @param {CopyToClipboardProps} props
 * @param {string} props.value - 클립보드로 복사돨 텍스트 값 전달
 * @returns {CopyToClipboardReturn} `{ copyState, setCopyState, copyToClipboard }`
 * - `copyToClipboard` : 클립보드 복사 기능을 제공하기 위한 함수
 * - `copyState`: 현재 복사 상태를 제공하는 객체 (클립보드 value, 복사 상태 메시지, noUserInteraction와 error 정보 제공)
 * - `setCopyState`: 현재 복사 상태를 제공하는 객체 (클립보드 value, 복사 상태 메시지, noUserInteraction와 error 정보 제공)
 *
 * @example
 * const { copyToClipboard, copyState, setCopyState } = useCopyToClipboard({ value });
 */
const useCopyToClipboard = ({ value }: CopyToClipboardProps): CopyToClipboardReturn => {
  const [copyState, setCopyState] = useState<CopyState>({
    value: null,
    error: null,
  });

  const copyToClipboard = useCallback(() => {
    try {
      if (value) navigator.clipboard.writeText(value);
      setCopyState({ value, error: null });
    } catch (error) {
      setCopyState({ value: value, error: error });
    }
  }, [value]);

  return {
    copyToClipboard,
    copyState,
    setCopyState,
  };
};

export default useCopyToClipboard;
